import React from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { PieChart, Pie, Cell, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { Skeleton } from "@/components/ui/skeleton";

const COLORS = ["#0088FE", "#00C49F", "#FFBB28", "#FF8042"];

interface Bike {
  id: string;
  status?: string;
  bikeStatus?: string;
}

interface BikeStatusChartProps {
  isLoading: boolean;
  bikes: any[];
}

const BikeStatusChart: React.FC<BikeStatusChartProps> = ({ isLoading, bikes }) => {
  const bikeUsageData = React.useMemo(() => {
    if (!bikes) return [];
    
    const statusCounts: Record<string, number> = {
      active: 0,
      inactive: 0,
      inUse: 0
    };
    
    bikes.forEach((bike) => {
      if (bike.bikeStatus === 'active' || bike.status === 'active') statusCounts.active++;
      else if (bike.bikeStatus === 'inactive' || bike.status === 'inactive') statusCounts.inactive++;
      else statusCounts.inUse++;
    });
    
    return [
      { name: 'Active', value: statusCounts.active },
      { name: 'Inactive', value: statusCounts.inactive },
      { name: 'In Use', value: statusCounts.inUse }
    ];
  }, [bikes]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>eBike Status Distribution</CardTitle>
        <CardDescription>
          Current status of all eBikes in the fleet
        </CardDescription>
      </CardHeader>
      <CardContent className="h-[300px]">
        {isLoading ? (
          <Skeleton className="h-full w-full" />
        ) : bikeUsageData.length === 0 ? (
          <div className="flex h-full items-center justify-center">
            <p className="text-muted-foreground">No bike data available</p>
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={bikeUsageData}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                outerRadius={100}
                fill="#8884d8"
                dataKey="value"
              >
                {bikeUsageData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        )}
      </CardContent>
    </Card>
  );
};

export default BikeStatusChart;
